/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Save, Loader2, Image as ImageIcon, Upload } from "lucide-react";
import { toast } from "sonner";
import { billetterieApi, formatApiError } from "@/lib/api";

const EMPTY_EVENT = {
  name: "DEMO", slug: "", tagline: "", description: "",
  date: "", end_date: "", time: "",
  venue_name: "", venue_address: "", city: "",
  hero_image_url: "",
  hero_no_overlay: false,
  primary_color: "#FF3D71", secondary_color: "#7C3AED",
  bg_gradient: "linear-gradient(135deg, #FF3D71 0%, #FF8C42 50%, #7C3AED 100%)",
  is_published: false,
  ticket_types: [
    { name: "VIP", description: "Accès backstage, boisson offerte", price_ttc: 60, color: "#FFD700", icon: "crown", sort_order: 0, is_active: true, quantity_available: null },
    { name: "Adulte", description: "Entrée standard", price_ttc: 25, color: "#FF3D71", icon: "ticket", sort_order: 1, is_active: true, quantity_available: null },
    { name: "Enfant", description: "Moins de 12 ans", price_ttc: 10, color: "#7C3AED", icon: "smile", sort_order: 2, is_active: true, quantity_available: null },
    { name: "Famille", description: "2 adultes + 2 enfants", price_ttc: 60, color: "#06B6D4", icon: "users", sort_order: 3, is_active: true, quantity_available: null },
  ],
};

const GRADIENTS = [
  ["Néon Rose", "linear-gradient(135deg, #FF3D71 0%, #FF8C42 50%, #7C3AED 100%)"],
  ["Tropical", "linear-gradient(135deg, #FFD700 0%, #FF6B6B 50%, #4ECDC4 100%)"],
  ["Sunset", "linear-gradient(135deg, #FA709A 0%, #FEE140 100%)"],
  ["Electric", "linear-gradient(135deg, #00DBDE 0%, #FC00FF 100%)"],
  ["Or & Nuit", "linear-gradient(135deg, #DCB457 0%, #050505 100%)"],
];

export default function AdminBilletterieEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState(EMPTY_EVENT);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (isNew) return;
    billetterieApi.adminGet(id).then((d) => setForm(d)).catch((e) => { toast.error(formatApiError(e)); navigate("/admin/billetterie"); }).finally(() => setLoading(false));
  }, [id, isNew, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updTicket = (i, k, v) => setForm((f) => {
    const tt = [...f.ticket_types];
    tt[i] = { ...tt[i], [k]: k === "price_ttc" || k === "quantity_available" ? (v === "" ? null : Number(v)) : v };
    return { ...f, ticket_types: tt };
  });
  const addTicket = () => setForm((f) => ({ ...f, ticket_types: [...f.ticket_types, { name: "Nouveau", price_ttc: 0, color: "#DCB457", icon: "ticket", sort_order: f.ticket_types.length, is_active: true, quantity_available: null }] }));
  const rmTicket = (i) => setForm((f) => ({ ...f, ticket_types: f.ticket_types.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Nom requis.");
    if (!form.date) return toast.error("Date requise.");
    setSaving(true);
    try {
      const res = isNew ? await billetterieApi.adminCreate(form) : await billetterieApi.adminUpdate(id, form);
      toast.success("Événement enregistré.");
      if (isNew) navigate(`/admin/billetterie/${res.id}`);
      else setForm(res);
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Fichier invalide — sélectionnez une image.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 10 Mo).");
      return;
    }
    setUploading(true);
    try {
      const res = await billetterieApi.adminUploadImage(file);
      update("hero_image_url", res.url);
      toast.success("Image téléversée.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-white/55"><Loader2 className="animate-spin" /> Chargement…</div>;

  return (
    <div data-testid="admin-bill-editor">
      <header className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <Link to="/admin/billetterie" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-white/55 hover:text-[#DCB457]"><ArrowLeft size={11} /> Retour</Link>
          <h1 className="font-serif-luxe text-3xl md:text-4xl mt-3">{isNew ? "Nouvel" : "Éditer"} <span className="italic gold-text">événement.</span></h1>
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-bill-save" className="btn-gold inline-flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
        </button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="card-luxe p-5 md:p-6 space-y-3">
            <p className="eyebrow">Informations</p>
            <input className="input-luxe" placeholder="Nom du festival *" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="admin-bill-name" />
            <input className="input-luxe" placeholder="Slug (optionnel — auto)" value={form.slug || ""} onChange={(e) => update("slug", e.target.value)} />
            <input className="input-luxe" placeholder="Accroche / Sous-titre" value={form.tagline || ""} onChange={(e) => update("tagline", e.target.value)} />
            <textarea className="input-luxe min-h-[120px]" placeholder="Description complète" value={form.description || ""} onChange={(e) => update("description", e.target.value)} />
          </div>

          <div className="card-luxe p-5 md:p-6 space-y-3">
            <p className="eyebrow">Date & lieu</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input type="date" className="input-luxe" value={form.date} onChange={(e) => update("date", e.target.value)} data-testid="admin-bill-date" />
              <input type="date" className="input-luxe" placeholder="Date fin (optionnel)" value={form.end_date || ""} onChange={(e) => update("end_date", e.target.value)} />
              <input className="input-luxe" placeholder="Horaires (ex 18h-02h)" value={form.time || ""} onChange={(e) => update("time", e.target.value)} />
            </div>
            <input className="input-luxe" placeholder="Nom de la salle / Lieu" value={form.venue_name || ""} onChange={(e) => update("venue_name", e.target.value)} />
            <input className="input-luxe" placeholder="Adresse complète" value={form.venue_address || ""} onChange={(e) => update("venue_address", e.target.value)} />
            <input className="input-luxe" placeholder="Ville" value={form.city || ""} onChange={(e) => update("city", e.target.value)} />
          </div>

          <div className="card-luxe p-5 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">Types de billets</p>
              <button onClick={addTicket} className="text-xs inline-flex items-center gap-1.5 text-[#DCB457] border border-[#DCB457]/40 px-2.5 py-1 hover:bg-[#DCB457]/10"><Plus size={11} /> Type</button>
            </div>
            <div className="space-y-3">
              {form.ticket_types.map((t, i) => (
                <div key={i} className="border border-[#DCB457]/15 p-3 space-y-2 relative" style={{ borderLeft: `3px solid ${t.color || "#DCB457"}` }}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                    <input className="input-luxe md:col-span-4" placeholder="Nom (VIP, Adulte…)" value={t.name} onChange={(e) => updTicket(i, "name", e.target.value)} />
                    <input className="input-luxe md:col-span-2" type="number" step="0.5" placeholder="Prix TTC" value={t.price_ttc} onChange={(e) => updTicket(i, "price_ttc", e.target.value)} />
                    <input className="input-luxe md:col-span-2" type="number" min="1" placeholder="Stock (vide=∞)" value={t.quantity_available ?? ""} onChange={(e) => updTicket(i, "quantity_available", e.target.value)} />
                    <input className="input-luxe md:col-span-2" type="color" value={t.color || "#DCB457"} onChange={(e) => updTicket(i, "color", e.target.value)} />
                    <label className="md:col-span-1 flex items-center gap-1 text-xs text-white/65">
                      <input type="checkbox" checked={t.is_active} onChange={(e) => updTicket(i, "is_active", e.target.checked)} className="accent-[#DCB457]" />
                      Actif
                    </label>
                    <button onClick={() => rmTicket(i)} className="md:col-span-1 text-red-300 hover:bg-red-500/10 border border-red-400/30 px-2 py-1 text-xs"><Trash2 size={11} /></button>
                  </div>
                  <input className="input-luxe" placeholder="Description courte" value={t.description || ""} onChange={(e) => updTicket(i, "description", e.target.value)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right aside: theme */}
        <aside className="space-y-5">
          <div className="card-luxe p-5">
            <p className="eyebrow mb-3">Apparence</p>

            <label className="text-xs text-white/55">Image / photo (upload)</label>
            <div className="mt-1 flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleImageUpload}
                className="hidden"
                data-testid="admin-bill-image-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="admin-bill-image-upload-btn"
                className="w-full inline-flex items-center justify-center gap-2 text-xs px-3 py-2.5 border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10 disabled:opacity-60 transition-colors"
              >
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {uploading ? "Téléversement…" : (form.hero_image_url ? "Remplacer l'image" : "Téléverser une image")}
              </button>
              <p className="text-[10px] text-white/40 leading-relaxed">
                JPG, PNG, WEBP ou GIF — max 10 Mo. L'image est hébergée sur notre stockage sécurisé.
              </p>
            </div>

            <label className="text-xs text-white/55 mt-4 block">Ou coller une URL externe</label>
            <input className="input-luxe mt-1" placeholder="https://..." value={form.hero_image_url || ""} onChange={(e) => update("hero_image_url", e.target.value)} data-testid="admin-bill-hero-url" />
            {form.hero_image_url && (
              <>
                <button
                  type="button"
                  onClick={() => update("hero_image_url", "")}
                  className="mt-2 text-[10px] uppercase tracking-[0.18em] text-white/45 hover:text-red-300"
                >
                  Retirer l'image
                </button>
                <label className="mt-3 flex items-start gap-2 text-xs text-white/75 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.hero_no_overlay}
                    onChange={(e) => update("hero_no_overlay", e.target.checked)}
                    data-testid="admin-bill-no-overlay"
                    className="mt-0.5 accent-[#DCB457] w-4 h-4"
                  />
                  <span>
                    Afficher l'image sans filtre
                    <span className="block text-[10px] text-white/40 mt-0.5">Le dégradé n'est pas superposé — l'image apparaît nette (utile pour flyers ou affiches).</span>
                  </span>
                </label>
              </>
            )}

            <div className="mt-4 grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-white/55">Primaire</label>
                <input type="color" className="input-luxe w-full mt-1 h-10" value={form.primary_color} onChange={(e) => update("primary_color", e.target.value)} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-white/55">Secondaire</label>
                <input type="color" className="input-luxe w-full mt-1 h-10" value={form.secondary_color} onChange={(e) => update("secondary_color", e.target.value)} />
              </div>
            </div>

            <p className="text-[10px] uppercase tracking-[0.18em] text-white/55 mt-4 mb-2">Dégradé prêt à l'emploi</p>
            <div className="space-y-2">
              {GRADIENTS.map(([name, grad]) => (
                <button key={name} onClick={() => update("bg_gradient", grad)} className="w-full text-left px-3 py-2 text-xs text-white border border-white/15 hover:border-[#DCB457]/60" style={{ background: grad }}>
                  <span className="bg-black/40 px-1.5 py-0.5 rounded">{name}</span>
                </button>
              ))}
            </div>

            <div className="mt-4 h-24 rounded relative overflow-hidden" style={{ background: form.hero_no_overlay && form.hero_image_url ? "#0a0a0a" : form.bg_gradient }}>
              {form.hero_image_url && <img src={form.hero_image_url} alt="" className={`absolute inset-0 w-full h-full object-cover ${form.hero_no_overlay ? "opacity-100" : "opacity-40"}`} />}
              <div className="absolute inset-0 flex items-center justify-center text-white font-serif-luxe text-xl drop-shadow">Aperçu</div>
            </div>
          </div>

          <div className="card-luxe p-5">
            <p className="eyebrow mb-3">Publication</p>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_published} onChange={(e) => update("is_published", e.target.checked)} data-testid="admin-bill-published" className="accent-[#DCB457] w-4 h-4" />
              <span className="text-white/85">Visible sur /billetterie</span>
            </label>
            <p className="text-[11px] text-white/45 mt-2 leading-relaxed">
              Tant que la case est décochée, seuls les administrateurs voient l'événement.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
